// Phase 2 §7: Extended CapTableChart with mode support
import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Loader2 } from 'lucide-react';
import { useCapTableSummary } from '@/hooks/useCapTableSummary';
import { useProjectionMode } from '@/hooks/useProjectionMode';
import { CapTableModeToggle } from './CapTableModeToggle';
import { ScenarioWatermark } from './ScenarioWatermark';
import { cn } from '@/lib/utils';

const COLORS = [
  'hsl(221.2 83.2% 53.3%)',
  'hsl(142.1 76.2% 36.3%)',
  'hsl(24.6 95% 53.1%)',
  'hsl(262.1 83.3% 57.8%)',
  'hsl(330.4 81.2% 60.4%)',
  'hsl(173.4 80.4% 40%)',
  'hsl(37.7 92.1% 50.2%)',
];

interface CapTableChartProps {
  companyId?: string;
}

export function CapTableChart({ companyId }: CapTableChartProps) {
  const { mode, isScenarioMode } = useProjectionMode();

  // Memoize the state type filter to prevent infinite re-renders
  const stateTypeFilter = useMemo(() => {
    return mode === 'LEGAL' ? 'LEGAL' : ['PROJECTION', 'SCENARIO'];
  }, [mode]);

  const { summary, loading, totalShares } = useCapTableSummary(companyId, stateTypeFilter);

  if (loading) {
    return (
      <Card className={cn('relative', isScenarioMode && 'border-l-4 border-l-warning')}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Shareholder Distribution {isScenarioMode && '(Projection)'}</CardTitle>
          <CapTableModeToggle showLabels={false} />
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[280px]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!summary.length || totalShares === 0) {
    return (
      <Card className={cn('relative', isScenarioMode && 'border-l-4 border-l-warning')}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Shareholder Distribution {isScenarioMode && '(Projection)'}</CardTitle>
          <CapTableModeToggle showLabels={false} />
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[280px] text-muted-foreground">
            No shareholder data yet
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = summary.map((entry) => ({
    name: entry.shareholder_name || 'Unknown',
    value: entry.total_shares || 0,
  }));

  return (
    <Card
      className={cn('relative overflow-hidden', isScenarioMode && 'border-l-4 border-l-warning')}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle>Shareholder Distribution {isScenarioMode && '(Projection)'}</CardTitle>
        <CapTableModeToggle showLabels={false} />
      </CardHeader>
      <CardContent>
        {isScenarioMode && <ScenarioWatermark />}
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              outerRadius={90}
              dataKey="value"
              label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
            >
              {chartData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => [`${value.toLocaleString()} shares`, 'Shares']}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
